import { useState, useMemo, useCallback, useEffect } from "react";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import usePagination from "@/hooks/usePagination";
import Select from "@/components/Select";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useQuery, useMutation } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Banner from "@/components/Banner";
import EmptyState from "@/components/EmptyState";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";
import { UNIVERSITY_ROSTER_ROWS_QUERY } from "./queries";
import { UNIVERSITY_ROSTER_ROWS_EXISTS_QUERY, ELIGIBLE_FOR_ENROLLMENT_ROSTER_STATUS } from "../queries";
import { UniversityRosterRowsQuery, EligibleStudent } from "./types";
import { useFinalizeEnrollments } from "./hooks";
import { UNIVERSITY_QUERY } from "../../queries";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { UPDATE_ROSTER_ROW, DELETE_ROSTER_ROW } from "../../../mutations";
import { ROSTER_PROGRAM_LEVEL_OPTIONS } from "../../../_shared/constants";
import { mapEligibleStudents, buildColumns, formatEligibleStudentsCopyText, isRosterPagePaginated } from "./_utils";
import RosterImportWarningsPanel from "./_components/RosterImportWarningsPanel";
import SearchInput from "@/components/SearchInput";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import { copyTextToClipboard } from "@/components/CopyButton";

export default function EligibleForEnrollmentPage() {
  const { universityId } = useParams();
  const navigate = useNavigate();
  const { isCompactLayout } = useIsCompactLayout();
  const { shouldShowPersonProfileActions } = useUniversityStaffPermissions();
  const [error, setError] = useState<string | null>(null);

  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const [selectedStudent, setSelectedStudent] = useState<EligibleStudent | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [dialog, setDialog] = useState<{
    type: "programLevel" | "remove" | "finalize" | null;
    data?: any;
  }>({ type: null });
  const [newProgramLevel, setNewProgramLevel] = useState("");

  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams();

  useEffect(() => {
    resetPagination();
  }, [debouncedSearch, resetPagination]);

  const { data: universityData, loading: universityLoading } = useQuery(UNIVERSITY_QUERY, {
    variables: { legacyId: universityId?.toString() || "" },
    skip: !universityId
  });

  const actualUniversityId = universityData?.universities[0]?.id;

  const rosterQueryVariables = {
    universityId: actualUniversityId || "",
    ...paginationVariables,
    rosterStatus: ELIGIBLE_FOR_ENROLLMENT_ROSTER_STATUS,
    includeErrors: true,
    search: debouncedSearch || undefined
  };

  const rosterExistsVariables = {
    universityId: actualUniversityId || "",
    rosterStatus: ELIGIBLE_FOR_ENROLLMENT_ROSTER_STATUS
  };

  const { data, loading: rosterLoading } = useQuery<UniversityRosterRowsQuery>(UNIVERSITY_ROSTER_ROWS_QUERY, {
    variables: rosterQueryVariables,
    skip: !actualUniversityId
  });

  const { data: rosterCountData } = useQuery(UNIVERSITY_ROSTER_ROWS_EXISTS_QUERY, {
    variables: rosterExistsVariables,
    skip: !actualUniversityId
  });

  const eligibleStudents = mapEligibleStudents(data);

  const rosterTotalCount = rosterCountData?.university?.universityRosterRows?.totalCount || 0;
  const rosterId = rosterCountData?.university?.universityRosterRows?.edges?.[0]?.node?.roster?.id;

  const { finalizeEnrollments, loading: finalizeLoading } = useFinalizeEnrollments({
    universityId: universityId || "",
    rosterTotalCount
  });

  const { Menu, setMenu } = useTableRowMenu();

  const rosterRefetchQueries = [
    { query: UNIVERSITY_ROSTER_ROWS_QUERY, variables: rosterQueryVariables },
    { query: UNIVERSITY_ROSTER_ROWS_EXISTS_QUERY, variables: rosterExistsVariables }
  ];

  const [updateRosterRow, { loading: updateLoading }] = useMutation(UPDATE_ROSTER_ROW, {
    refetchQueries: rosterRefetchQueries
  });

  const [deleteRosterRow, { loading: deleteLoading }] = useMutation(DELETE_ROSTER_ROW, {
    refetchQueries: rosterRefetchQueries
  });

  const handleViewStudent = useCallback((student: EligibleStudent) => {
    setSelectedStudent(student);
    setIsDetailsPanelOpen(true);
  }, []);

  const handleOpenProgramLevelDialog = useCallback((student: EligibleStudent) => {
    setDialog({ type: "programLevel", data: student });
    setNewProgramLevel(student.programLevel);
  }, []);

  const handleUpdateProgramLevel = async (studentId: string, newLevel: string) => {
    try {
      const result = await updateRosterRow({
        variables: {
          rosterRowId: studentId,
          input: {
            educationLevel: newLevel
          }
        }
      });

      if (result.data?.updateUniversityRosterRow?.success) {
        showSnackbar("Program level updated successfully");
        setDialog({ type: null });
        setNewProgramLevel("");
      } else {
        const errorMessage = result.data?.updateUniversityRosterRow?.message || "Failed to update program level";
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Failed to update program level:", error);
      setError("Failed to update program level. Please try again.");
    }
  };

  const handleCancelProgramLevelDialog = () => {
    setDialog({ type: null });
    setNewProgramLevel("");
    setError(null);
  };

  const handleRemoveStudent = async (studentId: string) => {
    try {
      await deleteRosterRow({
        variables: {
          rosterRowId: studentId
        }
      });
      showSnackbar("Student removed successfully");
      setDialog({ type: null });

      // Check if this was the last student and navigate if so
      if (rosterTotalCount <= 1) {
        showSnackbar("No more eligible students. Redirecting to current students.");
        navigate(`/education/universities/${universityId}/students`);
      }
    } catch (error) {
      console.error("Failed to remove student:", error);
      setError("Failed to remove student. Please try again.");
    }
  };

  const columns = useMemo(
    () =>
      buildColumns({
        isCompactLayout,
        handleViewStudent,
        handleOpenProgramLevelDialog,
        setDialog,
        setMenu,
        actionId,
        shouldShowPersonProfileActions
      }),
    [isCompactLayout, handleViewStudent, handleOpenProgramLevelDialog, setMenu, shouldShowPersonProfileActions]
  );

  const handleFinalizeEnrollments = () => {
    setDialog({ type: "finalize" });
  };

  const warningStudents = eligibleStudents.filter((student) => student.importWarnings.length > 0);
  const copyWarningRowsOnThisPage = isRosterPagePaginated(data?.university?.universityRosterRows?.pageInfo);
  const copyWarningRowsLabel = copyWarningRowsOnThisPage ? "Copy Warning Rows on This Page" : "Copy Warning Rows";

  const handleCopyWarningRows = async () => {
    if (warningStudents.length === 0) return;

    try {
      await copyTextToClipboard(formatEligibleStudentsCopyText(warningStudents));
      const rowLabel = `Copied ${warningStudents.length} warning row${warningStudents.length === 1 ? "" : "s"}`;
      showSnackbar(copyWarningRowsOnThisPage ? `${rowLabel} on this page` : rowLabel);
    } catch {
      showErrorSnackbar("Failed to copy warning rows.");
    }
  };

  const handleFinalizeEnrollmentsConfirm = async () => {
    if (!rosterId) {
      setError("Could not find roster information");
      return;
    }

    try {
      await finalizeEnrollments(rosterId);
      setDialog({ type: null });
    } catch (error) {
      console.error("Failed to finalize enrollments:", error);
      setError("Failed to finalize enrollments. Please try again.");
    }
  };

  const handleFinalizeEnrollmentsCancel = () => {
    setDialog({ type: null });
    setError(null);
  };

  return (
    <>
      <TableHeading
        heading="PGM Program Roster Eligible for Enrollment"
        body="PGA National shall review and finalize the enrollment status for all students associated with this PGM University Program roster."
        actions={[
          {
            label: "Finalize Enrollments",
            variant: "filled",
            onClick: handleFinalizeEnrollments,
            testId: "finalize-enrollments-button"
          },
          {
            label: copyWarningRowsLabel,
            variant: "outlined",
            icon: "content_copy",
            onClick: handleCopyWarningRows,
            disabled: warningStudents.length === 0,
            testId: "copy-warning-rows-button"
          }
        ]}
      />
      <Table
        data={eligibleStudents}
        loading={universityLoading || rosterLoading || updateLoading || deleteLoading}
        columns={columns}
        data-testid="eligible-students-table"
        hasActiveFilters={!!debouncedSearch}
        onClickRow={(row) => {
          handleViewStudent(row.original);
        }}
        getRowCanExpand={(row) => (row.original.importWarnings?.length || 0) > 0}
        renderExpandedRow={({ row }) => (
          <RosterImportWarningsPanel
            student={row.original}
            shouldShowPersonProfileActions={shouldShowPersonProfileActions}
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Matching Students" caption="Try a different search." iconName="search" />
        )}
        renderControls={() => (
          <TableControls
            pagination={createHeaderPagination(
              data?.university?.universityRosterRows
                ? {
                    ...data.university.universityRosterRows,
                    pageInfo: {
                      ...data.university.universityRosterRows.pageInfo,
                      startCursor: data.university.universityRosterRows.pageInfo.startCursor ?? undefined,
                      endCursor: data.university.universityRosterRows.pageInfo.endCursor ?? undefined
                    }
                  }
                : undefined
            )}
          >
            <SearchInput
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
              placeholder="Search Students"
            />
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(
              data?.university?.universityRosterRows
                ? {
                    ...data.university.universityRosterRows,
                    pageInfo: {
                      ...data.university.universityRosterRows.pageInfo,
                      startCursor: data.university.universityRosterRows.pageInfo.startCursor ?? undefined,
                      endCursor: data.university.universityRosterRows.pageInfo.endCursor ?? undefined
                    }
                  }
                : undefined
            )}
          />
        )}
      />

      <div className="flex items-start" data-testid="student-display">
        <PersonSideSheet
          open={isDetailsPanelOpen && !!selectedStudent}
          onClose={() => setIsDetailsPanelOpen(false)}
          pgaId={selectedStudent?.pgaId ?? null}
          title="Student Details"
          showPersonalDetails={true}
          universityName={universityData?.universities[0]?.name}
          customActionButtons={[]}
        />
      </div>

      <Dialog
        open={dialog.type === "finalize"}
        onClosed={handleFinalizeEnrollmentsCancel}
        headline="Finalize Enrollments"
        displayOverflow={true}
        actions={
          <>
            <Button variant="text" onClick={handleFinalizeEnrollmentsCancel}>
              Cancel
            </Button>
            <Button variant="filled" onClick={handleFinalizeEnrollmentsConfirm} disabled={finalizeLoading}>
              {finalizeLoading
                ? "Finalizing..."
                : `Finalize ${rosterTotalCount} Student${rosterTotalCount === 1 ? "" : "s"}`}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          {error && (
            <Banner variant="error" description={error} inline={true} closable={true} onClose={() => setError(null)} />
          )}
          <div>
            <p className="text-body-medium-semibold mb-4">
              Total Students to Finalize Enrollments: <strong>{rosterTotalCount}</strong>
            </p>
            <p>
              Are you sure you want to finalize the enrollments for this student roster? Please ensure that the student
              information is correct. The students will be auto-enrolled in Canvas courses, and their student membership
              bands will be started. Students who are not in Member 360 yet will have new profiles created.
            </p>
          </div>
        </div>
      </Dialog>
      <Menu />

      {/* Program Level Change Dialog */}
      <Dialog
        open={dialog.type === "programLevel"}
        onClosed={handleCancelProgramLevelDialog}
        headline="Change Program Level"
        displayOverflow={true}
        hasFormElements={true}
        actions={
          <>
            <Button variant="text" onClick={handleCancelProgramLevelDialog}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={() => dialog.data && handleUpdateProgramLevel(dialog.data.id, newProgramLevel)}
              disabled={!newProgramLevel || updateLoading}
            >
              {updateLoading ? "Updating..." : "Save"}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          {error && (
            <Banner variant="error" description={error} inline={true} closable={true} onClose={() => setError(null)} />
          )}
          <div>
            <p className="text-body-medium-semibold">
              Change Program Level For: <strong>{dialog.data?.name}</strong>
            </p>
            <p className="text-body-medium-semibold mb-4">
              Current Level: <strong>{dialog.data?.programLevel}</strong>
            </p>
          </div>

          <div>
            <Select
              label="New Program Level"
              options={ROSTER_PROGRAM_LEVEL_OPTIONS}
              value={newProgramLevel}
              onSelect={(value) => setNewProgramLevel(value)}
              disabled={updateLoading}
            />
          </div>
        </div>
      </Dialog>

      {/* Remove Student Dialog */}
      <Dialog
        open={dialog.type === "remove"}
        onClosed={() => {
          setDialog({ type: null });
          setError(null);
        }}
        headline="Remove Student"
        displayOverflow={true}
        actions={
          <>
            <Button
              variant="text"
              onClick={() => {
                setDialog({ type: null });
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={() => dialog.data && handleRemoveStudent(dialog.data.id)}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Removing..." : "Remove Student"}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          {error && (
            <Banner variant="error" description={error} inline={true} closable={true} onClose={() => setError(null)} />
          )}
          <div>
            <p className="text-body-medium text-sys-brand-on-surface mb-4">
              Are you sure you want to remove <strong>{dialog.data?.name}</strong> from the roster?
            </p>
            <p className="text-body-medium text-sys-brand-on-surface">
              This action cannot be undone. The student will be removed from the roster, and their enrollment status
              will be reset.
            </p>
          </div>
        </div>
      </Dialog>
    </>
  );
}
